import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Row, Col, Card, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrgDropDown } from "slices/department/thunk";
import { Axios } from "api/axios";
import { Autocomplete, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
import { autocompleteDropdownStyles } from "Constants/Style";

const DepartmentForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const commonInputStyle = {
    fontSize: "0.875rem",
    fontFamily: "Poppins, sans-serif",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };
  const commonLabelStyle = {
    fontSize: "0.8rem",
    fontFamily: "Poppins, sans-serif",
    marginBottom: 6,
    marginTop: 12,
  };
  const commonErrorStyle = {
    fontSize: "0.75rem",
    fontFamily: "Poppins, sans-serif",
    color: "#dc3545",
  };

  useEffect(() => {
    dispatch(fetchOrgDropDown());
  }, [dispatch]);

  const orgDropDown: any[] = useSelector(
    (state: any) => state.department.orgDropDown
  );
  const orgDropDownLoading = useSelector(
    (state: any) => state.department.orgDropDownLoading
  );

  const formik = useFormik({
    initialValues: {
      organization: "",
      department_name: "",
      organization_name: "",
      department_id: "",
    },
    validationSchema: Yup.object({
      organization: Yup.string().required("Organisation Name is required"),
      department_name: Yup.string().required("Department Name is required"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          organization_id: values.organization,
          department_name: values.department_name,
        };
        setIsSubmitting(true);
        let response;
        if (id) {
          response = await Axios.patch(`/department/${id}/update/`, payload);
        } else {
          response = await Axios.post("/department/create/", payload);
        }
        console.log("Response:", response); // <-- for debugging
        if (response.status === 200 || response.status === 201) {
          toast.success(
            `Department ${id ? "updated" : "created"} successfully`
          );
          // Clear state when creating new item, preserve when editing
          navigate("/departments-list", { state: { clearState: !id } });
        }
      } catch (error: any) {
        if (error?.response?.data?.error) {
          toast.error(error?.response?.data?.error);
        } else {
          toast.error("Something Went Wrong");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      setLoading(true);
      Axios.get(`/department/${id}/get/`)
        .then((res) => {
          const data = res?.data?.data;
          formik.setValues({
            organization: data?.organization_id || "",
            department_name: data?.department_name || "",
            organization_name: data?.organization_name || "",
            department_id: id,
          });
        })
        .catch((error) => console.log(error))
        .finally(() => setLoading(false));
    }
  }, [id]);

  const LoadingCard = () => (
    <div className="text-center py-5 bg-white rounded-3 shadow-sm">
      <Spinner animation="border" variant="primary" />
    </div>
  );

  return (
    <PageContainer>
      {/* Header Row: Title on Left, Breadcrumb on Right */}
      <FormHeader
        title={`${id ? "Edit" : "Add"} Department`}
        firstLabel="Department"
        secondLabel={id ? "Edit Department" : "Add Department"}
        handleArrowClick={() => navigate("/department-list")}
      />

        {loading ? (
          <LoadingCard />
        ) : (
          <>
            <Card className="p-4 shadow-sm rounded">
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Row className="mb-4">
                  {/* Organisation */}
                  <Col md={6} xs={12} className="mb-3 mb-md-0">
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Organisation Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      disablePortal
                      options={orgDropDown}
                      getOptionLabel={(option: any) =>
                        option.organization_name || ""
                      }
                      value={
                        orgDropDown.find(
                          (org) =>
                            org?.organization_id === formik.values.organization
                        ) || null
                      }
                      onChange={(_, value) => {
                        formik.setFieldValue(
                          "organization",
                          value?.organization_id || ""
                        );
                        formik.setFieldValue(
                          "organization_name",
                          value?.organization_name || ""
                        );
                        formik.validateField("organization"); // validate after change
                      }}
                      onBlur={() =>
                        formik.setFieldTouched("organization", true)
                      } // touched on blur only
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderOption={(props, option) =>
                        autocompleteDropdownStyles.renderOption(
                          props,
                          option,
                          (option: any) => option.organization_name || ""
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Organisation"
                          error={
                            formik.touched.organization &&
                            !!formik.errors.organization
                          }
                          helperText={
                            formik.touched.organization &&
                            formik.errors.organization
                          }
                          fullWidth
                          size="small"
                          autoComplete="off"
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiOutlinedInput-input": {
                              fontSize: "0.875rem",
                              fontFamily: "Poppins, sans-serif",
                              padding: "10.5px 14px",
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: "0.75rem",
                              fontFamily: "Poppins, sans-serif",
                              color: "#dc3545",
                            },
                          }}
                        />
                      )}
                    />
                  </Col>

                  {/* Department Name */}
                  <Col md={6} xs={12}>
                    <Form.Label
                      className="fw-semibold"
                      style={commonLabelStyle}
                    >
                      Department Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="department_name"
                      placeholder="Enter Department Name"
                      value={formik.values.department_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        formik.touched.department_name &&
                        !!formik.errors.department_name
                      }
                      style={commonInputStyle}
                    />
                    <Form.Control.Feedback
                      type="invalid"
                      style={commonErrorStyle}
                    >
                      {formik.errors.department_name}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                {/* Buttons */}
                <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
                  <CancelBackButton to="/departments-list" />

                  {/* Reusable submit button with spinner */}
                  <SubmitButton isSubmitting={isSubmitting} isEdit={!!id} />
                </div>
              </Form>
            </Card>
        </>
      )}
    </PageContainer>
  );
};

export default DepartmentForm;
